import client from '@/services/http/client'
import { isMockEnabled, mockDelay, paginate } from '@/services/http/mock'
import {
  canManageKnowledge,
  KNOWLEDGE_KIND_LABEL,
  KNOWLEDGE_PROBLEM_TYPE_LABEL,
  KNOWLEDGE_SOURCE_TYPE_LABEL,
  KNOWLEDGE_STATUS_LABEL,
  UNCLASSIFIED_PRODUCT_LABEL,
} from '@/modules/knowledge/constants'
import { useAuthStore } from '@/stores/authStore'
import type { PaginatedData } from '@/types/api'
import type {
  KnowledgeBrochureOption,
  KnowledgeCreatePayload,
  KnowledgeEntry,
  KnowledgeDetail,
  KnowledgeFilterOption,
  KnowledgeFilters,
  KnowledgeIndexStatus,
  KnowledgeKind,
  KnowledgeListParams,
  KnowledgeOverview,
  KnowledgeProblemType,
  KnowledgeProductStat,
  KnowledgeProducts,
  KnowledgeReindexResult,
  KnowledgeReasonCodes,
  KnowledgeReviewFacets,
  KnowledgeReviewQueue,
  KnowledgeReviewSummary,
  KnowledgeSearchPayload,
  KnowledgeSearchResult,
  KnowledgeSourceType,
  KnowledgeStatus,
  KnowledgeUpdatePayload,
} from '@/types/knowledge'

// ============== Mock 数据 ==============
// 产品主分类以产品彩页为准：彩页清单是唯一事实来源，没有知识的彩页也要能展示（count 为 0）
// 名称/文件名/摘要取自后端 catalog.BROCHURE_PRODUCTS，保持 mock 与线上口径一致
const MOCK_BROCHURES: KnowledgeBrochureOption[] = [
  {
    value: 'brochure:medical',
    label: '智慧医疗',
    industry: '智慧医疗',
    summary: '医院信息发布、导视导诊、病房电视与宣教系统',
    display_name: '清鹤智慧医疗手册.pdf',
  },
  {
    value: 'brochure:hotel',
    label: '智慧酒店',
    industry: '智慧酒店',
    summary: '酒店客房电视、IPTV、信息发布与智慧化场景方案',
    display_name: '清鹤智慧酒店解决方案.pdf',
  },
  {
    value: 'brochure:smart_media',
    label: '智慧传媒（信息发布）',
    industry: '智慧传媒',
    summary: '信息发布系统、数字标牌与传媒场景方案',
    display_name: '清鹤智慧传媒.pdf',
  },
]

// 行业分类，与后端 catalog.INDUSTRY_CATEGORIES 对齐
const MOCK_INDUSTRIES = [
  { value: 'hotel', label: '智慧酒店' },
  { value: 'scenic', label: '智慧景区' },
  { value: 'government', label: '智慧政务' },
  { value: 'medical', label: '智慧医疗' },
  { value: 'judicial', label: '智慧司法' },
]

function mockBrochure(productKey: string): KnowledgeBrochureOption | undefined {
  return MOCK_BROCHURES.find((item) => item.value === productKey)
}

type MockKnowledgeSeed = {
  id: number
  title: string
  content: string
  keywords: string[]
  kind: KnowledgeKind
  product_key: string
  zentao_product_id?: number
  model_names?: string[]
  problem_type: KnowledgeProblemType
  source_type: KnowledgeSourceType
  status: KnowledgeStatus
  author: string
  created_at: string
  updated_at: string
  source_ref?: string | null
  source_url?: string | null
  source_ticket_id?: number | null
  reviewer?: string | null
  review_note?: string | null
  ai_generated?: boolean
  is_indexed?: boolean
  index_error?: string | null
  published_at?: string | null
  // 对外版本（脱敏）。不传时沿用正文，见 buildMockEntry
  public_title?: string | null
  public_content?: string | null
  public_summary?: string | null
  public_state?: 'fresh' | 'stale' | 'missing'
}

/** 按后端 entry_detail 的结构拼装 mock 条目，标签统一取自 constants，避免与页面口径漂移 */
function buildMockEntry(seed: MockKnowledgeSeed): KnowledgeEntry {
  // 产品名与产品线（行业）由彩页清单派生，前端不维护第二份映射
  const brochure = mockBrochure(seed.product_key)
  return {
    ...seed,
    kind_label: KNOWLEDGE_KIND_LABEL[seed.kind],
    product_name: brochure?.label || UNCLASSIFIED_PRODUCT_LABEL,
    product_line: brochure?.industry || '',
    zentao_product_id: seed.zentao_product_id ?? 0,
    model_names: seed.model_names ?? [],
    // 审核链路字段：mock 也要给默认值，否则前端类型不完整
    ai_generated: seed.ai_generated ?? false,
    review_note: seed.review_note ?? null,
    // 对外版本：mock 不做真实脱敏，直接沿用正文，保证"有正文就有对外版本"这条不变量成立 ——
    // 否则 mock 里每条都显示"尚未生成对外版本"，与线上表现相反（线上发布时会自动补齐）。
    public_title: seed.public_title ?? seed.title,
    public_content: seed.public_content ?? seed.content,
    public_summary: seed.public_summary ?? null,
    public_state: seed.public_state ?? 'fresh',
    public_state_label: seed.public_state === 'missing' ? '未生成对外版' : '对外版已是最新',
    problem_type_label: KNOWLEDGE_PROBLEM_TYPE_LABEL[seed.problem_type],
    source_type_label: KNOWLEDGE_SOURCE_TYPE_LABEL[seed.source_type],
    source_ref: seed.source_ref ?? null,
    source_url: seed.source_url ?? null,
    source_ticket_id: seed.source_ticket_id ?? null,
    status_label: KNOWLEDGE_STATUS_LABEL[seed.status],
    reviewer: seed.reviewer ?? null,
    is_indexed: seed.is_indexed ?? false,
    index_error: seed.index_error ?? null,
    published_at: seed.published_at ?? null,
  }
}

const mockKnowledge: KnowledgeEntry[] = [
  buildMockEntry({
    id: 1,
    title: '报到机"未在班"提示排查方法',
    content: '排查排班服务器连接状态，确认科室排班表是否正确同步，检查报到机网络配置和软件版本。',
    keywords: ['报到机', '排班', '未在班'],
    kind: 'issue',
    product_key: 'brochure:medical',
    zentao_product_id: 108,
    model_names: ['QR-2000', 'QR-2000B'],
    problem_type: 'data_inconsistency',
    source_type: 'closed_ticket',
    source_ref: 'wr12345:ITEM-0001',
    source_ticket_id: 12345,
    status: 'published',
    author: 'svc_a',
    reviewer: 'mgr_a',
    is_indexed: true,
    published_at: '2026-05-10T16:00:00+08:00',
    created_at: '2026-05-09T10:00:00+08:00',
    updated_at: '2026-05-10T16:00:00+08:00',
  }),
  buildMockEntry({
    id: 2,
    title: 'E-500打印机错误标准处理流程',
    content: '检查打印机IP配置，确认网络连通性，重启打印服务，如仍无法解决则联系厂商更换硬件。',
    keywords: ['打印机', 'E-500', '硬件故障'],
    kind: 'issue',
    product_key: 'brochure:medical',
    zentao_product_id: 108,
    model_names: ['E-500'],
    problem_type: 'hardware',
    source_type: 'zentao_task',
    source_ref: 'task:2665',
    source_url: 'https://zentao.example.com/task-view-2665.html',
    source_ticket_id: 12300,
    status: 'published',
    author: 'tech_b',
    reviewer: 'mgr_a',
    is_indexed: true,
    index_error: null,
    published_at: '2026-05-09T14:30:00+08:00',
    created_at: '2026-05-08T09:00:00+08:00',
    updated_at: '2026-05-09T14:30:00+08:00',
  }),
  buildMockEntry({
    id: 3,
    title: '数据库连接超时排查手册',
    content: '检查连接池配置，确认数据库服务器负载，查看慢查询日志，必要时增加连接池大小或优化SQL。',
    keywords: ['数据库', '连接超时', '性能'],
    kind: 'issue',
    product_key: '',
    problem_type: 'software_bug',
    source_type: 'manual',
    source_ticket_id: 12250,
    status: 'draft',
    author: 'tech_a',
    is_indexed: false,
    index_error: 'EmbeddingError: 向量服务连接超时',
    created_at: '2026-05-08T08:00:00+08:00',
    updated_at: '2026-05-08T10:00:00+08:00',
  }),
  buildMockEntry({
    id: 4,
    title: '报到机支持的门禁卡类型与刷卡流程',
    content: '报到机支持院内一卡通与身份证两种介质。刷一卡通时先校验卡状态，再写排班记录。',
    keywords: ['报到机', '门禁卡', '一卡通'],
    kind: 'capability',
    product_key: 'brochure:medical',
    zentao_product_id: 4,
    model_names: ['QR-2000'],
    problem_type: 'usage',
    source_type: 'document',
    source_ref: 'doc:reception-reader-manual',
    status: 'published',
    author: 'tech_b',
    reviewer: 'mgr_a',
    is_indexed: true,
    published_at: '2026-05-11T09:00:00+08:00',
    created_at: '2026-05-11T08:30:00+08:00',
    updated_at: '2026-05-11T09:00:00+08:00',
  }),
]

const MOCK_VECTOR_DEGRADED_REASON = 'embedding 或向量库未配置'

function mockFilterOptions(
  values: string[],
  labels: Record<string, string>,
): KnowledgeFilterOption[] {
  const counts = new Map<string, number>()
  for (const value of values) {
    counts.set(value, (counts.get(value) || 0) + 1)
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: labels[value] || value, count }))
    .sort((left, right) => right.count - left.count)
}

/** 与后端 list_filter_options 一致：只统计非归档条目 */
function mockActiveEntries(): KnowledgeEntry[] {
  return mockKnowledge.filter((entry) => entry.status !== 'archived')
}

function mockFilters(): KnowledgeFilters {
  const active = mockActiveEntries()
  return {
    products: MOCK_BROCHURES.map((brochure) => ({
      product_key: brochure.value,
      product_name: brochure.label,
      product_line: brochure.industry,
      count: active.filter((entry) => entry.product_key === brochure.value).length,
    })).filter((product) => product.count > 0),
    kinds: mockFilterOptions(
      active.map((entry) => entry.kind),
      KNOWLEDGE_KIND_LABEL,
    ),
    problem_types: mockFilterOptions(
      active.map((entry) => entry.problem_type),
      KNOWLEDGE_PROBLEM_TYPE_LABEL,
    ),
    source_types: mockFilterOptions(
      active.map((entry) => entry.source_type),
      KNOWLEDGE_SOURCE_TYPE_LABEL,
    ),
  }
}

/** /knowledge/products 的 mock：彩页清单全量下发（含 0 条），未归类单独一档，与后端一致 */
function mockProductStats(): KnowledgeProductStat[] {
  const active = mockActiveEntries()
  const problemTypeOptions = (entries: KnowledgeEntry[]) =>
    mockFilterOptions(
      entries.map((entry) => entry.problem_type),
      KNOWLEDGE_PROBLEM_TYPE_LABEL,
    )

  const items: KnowledgeProductStat[] = MOCK_BROCHURES.map((brochure) => {
    const entries = active.filter((entry) => entry.product_key === brochure.value)
    return {
      product_key: brochure.value,
      product_name: brochure.label,
      product_line: brochure.industry,
      summary: brochure.summary,
      display_name: brochure.display_name,
      count: entries.length,
      by_problem_type: problemTypeOptions(entries),
    }
  })

  const unclassified = active.filter((entry) => !entry.product_key)
  if (unclassified.length > 0) {
    items.unshift({
      product_key: '',
      product_name: UNCLASSIFIED_PRODUCT_LABEL,
      product_line: '',
      summary: '只识别到行业，未落到具体彩页产品；补齐群绑定或人工归类可提升精度。',
      display_name: '',
      count: unclassified.length,
      by_problem_type: problemTypeOptions(unclassified),
    })
  }
  return items
}

function mockIndustries(): KnowledgeFilterOption[] {
  const active = mockActiveEntries()
  return MOCK_INDUSTRIES.map((industry) => ({
    ...industry,
    count: active.filter((entry) => mockBrochure(entry.product_key)?.industry === industry.label).length,
  }))
}

type MockListFilters = {
  keyword?: string
  status?: KnowledgeStatus
  kind?: KnowledgeKind
  product_key?: string
  product_line?: string
  problem_type?: KnowledgeProblemType
  source_type?: KnowledgeSourceType
}

function filterMockEntries(filters: MockListFilters): KnowledgeEntry[] {
  let filtered = mockKnowledge
  if (filters.status) {
    filtered = filtered.filter((entry) => entry.status === filters.status)
  }
  if (filters.kind) {
    filtered = filtered.filter((entry) => entry.kind === filters.kind)
  }
  // 空串是有效取值，语义为"未归属到任何彩页产品"，因此只判 undefined
  if (filters.product_key !== undefined) {
    filtered = filtered.filter((entry) => entry.product_key === filters.product_key)
  }
  if (filters.product_line) {
    filtered = filtered.filter((entry) => entry.product_line === filters.product_line)
  }
  if (filters.problem_type) {
    filtered = filtered.filter((entry) => entry.problem_type === filters.problem_type)
  }
  if (filters.source_type) {
    filtered = filtered.filter((entry) => entry.source_type === filters.source_type)
  }
  if (filters.keyword) {
    const kw = filters.keyword.toLowerCase()
    filtered = filtered.filter(
      (entry) =>
        entry.title.toLowerCase().includes(kw) ||
        entry.content.toLowerCase().includes(kw) ||
        entry.keywords.some((keyword) => keyword.toLowerCase().includes(kw)),
    )
  }
  return filtered
}

// ============== API 方法 ==============
export async function fetchKnowledgeEntries(
  params: KnowledgeListParams,
): Promise<PaginatedData<KnowledgeEntry>> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    // 与后端一致：非 manager/admin 只能看已发布知识，显式请求其他状态直接拒绝
    if (params.status && params.status !== 'published' && !canManageKnowledge(roles)) {
      throw Object.assign(new Error('仅 manager/admin 可浏览未发布知识条目'), { code: 'FORBIDDEN' })
    }
    const status = params.status ?? (canManageKnowledge(roles) ? undefined : 'published')
    const filtered = filterMockEntries({ ...params, status })
    return paginate(filtered, params.page || 1, params.page_size || 20)
  }
  const { data } = await client.get<PaginatedData<KnowledgeEntry>>('/knowledge', { params })
  return data
}

export async function fetchKnowledgeDetail(entryId: number): Promise<KnowledgeDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const detail = mockKnowledge.find((k) => k.id === entryId)
    if (!detail) throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    const { roles } = useAuthStore.getState()
    if (detail.status !== 'published' && !canManageKnowledge(roles)) {
      throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    }
    return { ...detail }
  }
  const { data } = await client.get<KnowledgeDetail>(`/knowledge/${entryId}`)
  return data
}

export async function fetchKnowledgeOverview(status?: string): Promise<KnowledgeOverview> {
  if (isMockEnabled()) {
    await mockDelay()
    const total = mockKnowledge.length
    const indexed = mockKnowledge.filter((entry) => entry.is_indexed).length
    const byStatus: Partial<Record<KnowledgeStatus, number>> = {}
    const byKind: KnowledgeOverview['summary']['by_kind'] = {}
    for (const entry of mockKnowledge) {
      byStatus[entry.status] = (byStatus[entry.status] || 0) + 1
      byKind[entry.kind] = {
        count: (byKind[entry.kind]?.count || 0) + 1,
        label: KNOWLEDGE_KIND_LABEL[entry.kind],
      }
    }
    return {
      summary: {
        total,
        indexed,
        pending_index: total - indexed,
        unclassified: mockKnowledge.filter((entry) => !entry.product_key).length,
        // 已归属到彩页产品的产品数量（与后端一致：不去重状态，按 product_key 去重）
        product_coverage: new Set(
          mockKnowledge.filter((entry) => entry.product_key).map((entry) => entry.product_key),
        ).size,
        by_status: byStatus,
        by_kind: byKind,
      },
      filters: mockFilters(),
    }
  }
  const { data } = await client.get<KnowledgeOverview>('/knowledge/overview', {
    // 统计口径必须与列表一致，否则面板数字和点进去的结果对不上
    params: status ? { status } : undefined,
  })
  return data
}

export async function fetchKnowledgeProducts(status?: string): Promise<KnowledgeProducts> {
  if (isMockEnabled()) {
    await mockDelay()
    const filters = mockFilters()
    return {
      items: mockProductStats(),
      kinds: filters.kinds,
      source_types: filters.source_types,
      problem_types: filters.problem_types,
      industries: mockIndustries(),
    }
  }
  const { data } = await client.get<KnowledgeProducts>('/knowledge/products', {
    params: status ? { status } : undefined,
  })
  return data
}

export async function searchKnowledge(
  payload: KnowledgeSearchPayload,
): Promise<KnowledgeSearchResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    if (payload.status && payload.status !== 'published' && !canManageKnowledge(roles)) {
      throw Object.assign(new Error('仅 manager/admin 可检索未发布知识条目'), { code: 'FORBIDDEN' })
    }
    const status = payload.status ?? (canManageKnowledge(roles) ? undefined : 'published')
    const topK = payload.top_k || 20
    // mock 环境没有向量库，行为与后端"未配置向量库"分支一致：降级关键词
    const items = filterMockEntries({ ...payload, status, keyword: payload.query }).slice(0, topK)
    return {
      mode: 'keyword',
      degraded_reason: MOCK_VECTOR_DEGRADED_REASON,
      recall: [],
      query: payload.query,
      items: items.map((entry) => ({ ...entry, score: null })),
    }
  }
  const { data } = await client.post<KnowledgeSearchResult>('/knowledge/search', payload)
  return data
}

export async function createKnowledge(payload: KnowledgeCreatePayload): Promise<KnowledgeEntry> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles, actor } = useAuthStore.getState()
    if (!canManageKnowledge(roles) && !roles.includes('service_agent') && !roles.includes('tech_owner')) {
      throw Object.assign(new Error('当前角色无录入权限'), { code: 'FORBIDDEN' })
    }
    if (payload.publish && !canManageKnowledge(roles)) {
      throw Object.assign(new Error('仅 manager/admin 可直接发布'), { code: 'FORBIDDEN' })
    }
    const now = new Date().toISOString()
    const entry = buildMockEntry({
      id: Math.max(...mockKnowledge.map((item) => item.id)) + 1,
      title: payload.title,
      content: payload.content,
      keywords: payload.keywords,
      kind: payload.kind,
      product_key: payload.product_key,
      problem_type: payload.problem_type,
      source_type: 'manual',
      source_url: payload.source_url ?? null,
      status: payload.publish ? 'published' : 'draft',
      author: actor || 'unknown',
      reviewer: payload.publish ? actor || 'unknown' : null,
      published_at: payload.publish ? now : null,
      created_at: now,
      updated_at: now,
    })
    mockKnowledge.push(entry)
    return { ...entry }
  }
  const { data } = await client.post<KnowledgeEntry>('/knowledge', payload)
  return data
}

export async function updateKnowledge(
  entryId: number,
  payload: KnowledgeUpdatePayload,
): Promise<KnowledgeEntry> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    if (!canManageKnowledge(roles) && !roles.includes('service_agent') && !roles.includes('tech_owner')) {
      throw Object.assign(new Error('当前角色无编辑权限'), { code: 'FORBIDDEN' })
    }
    const entry = mockKnowledge.find((k) => k.id === entryId)
    if (!entry) throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    // 只合并显式传入的字段，与后端 exclude_unset 语义一致
    const changes = Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined),
    ) as KnowledgeUpdatePayload
    const updated = buildMockEntry({
      ...entry,
      ...changes,
      id: entry.id,
      source_type: entry.source_type,
      status: entry.status,
      author: entry.author,
      created_at: entry.created_at,
      updated_at: new Date().toISOString(),
      // 正文变化后需要重建向量索引，与后端 apply_entry_update 一致
      is_indexed: changes.content && changes.content !== entry.content ? false : entry.is_indexed,
      index_error: changes.content && changes.content !== entry.content ? null : entry.index_error,
    })
    mockKnowledge[mockKnowledge.indexOf(entry)] = updated
    return { ...updated }
  }
  const { data } = await client.patch<KnowledgeEntry>(`/knowledge/${entryId}`, payload)
  return data
}

export async function publishKnowledge(
  entryId: number,
  options: { reviewer?: string; note?: string; reasonCode?: string } = {}
): Promise<KnowledgeDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles, actor } = useAuthStore.getState()
    if (!canManageKnowledge(roles)) {
      throw Object.assign(new Error('当前角色无发布权限'), { code: 'FORBIDDEN' })
    }
    const entry = mockKnowledge.find((k) => k.id === entryId)
    if (!entry) throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    if (entry.status === 'published') {
      throw Object.assign(new Error('该知识条目已发布，不可重复发布'), { code: 'CONFLICT' })
    }
    // 与后端同一条硬约束：AI 归纳内容必须给出审核说明才能发布
    if (entry.ai_generated && !options.note?.trim()) {
      throw Object.assign(new Error('AI 归纳内容必须填写审核说明后才能发布'), { code: 'VALIDATION_ERROR' })
    }
    entry.status = 'published'
    entry.status_label = KNOWLEDGE_STATUS_LABEL.published
    entry.published_at = new Date().toISOString()
    entry.reviewer = options.reviewer || actor || 'mgr_a'
    entry.review_note = options.note?.trim() || null
    return { ...entry }
  }
  const { data } = await client.post<KnowledgeDetail>(`/knowledge/${entryId}/publish`, {
    reviewer: options.reviewer,
    note: options.note,
    reason_code: options.reasonCode,
  })
  return data
}

export async function regeneratePublicVersion(entryId: number): Promise<KnowledgeDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const entry = mockKnowledge.find((k) => k.id === entryId)
    if (!entry) throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    return { ...entry }
  }
  const { data } = await client.post<KnowledgeDetail>(`/knowledge/${entryId}/public-version`)
  return data
}

/**
 * 归档（退回 / 下架）单条知识。
 *
 * ``note`` / ``reasonCode`` **都可选**：与后端 ``ArchiveKnowledgeRequest`` 同一口径 ——
 * 不填时后端自动写一条事实流水。业务方 2026-09-17 明确去掉必填理由；
 * ``reasonCode`` 是一次点击的结构化理由，用来给后续打分积累金标（不点也能保存）。
 */
export async function archiveKnowledge(
  entryId: number,
  options: { note?: string; reasonCode?: string } = {},
): Promise<KnowledgeDetail> {
  const { note, reasonCode } = options
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    if (!canManageKnowledge(roles)) {
      throw Object.assign(new Error('当前角色无归档权限'), { code: 'FORBIDDEN' })
    }
    const entry = mockKnowledge.find((k) => k.id === entryId)
    if (!entry) throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    entry.status = 'archived'
    entry.status_label = KNOWLEDGE_STATUS_LABEL.archived
    entry.review_note = note?.trim() || `归档 1 条；未填说明`
    return { ...entry }
  }
  const { data } = await client.post<KnowledgeDetail>(`/knowledge/${entryId}/archive`, {
    note,
    reason_code: reasonCode,
  })
  return data
}

// ── 审核队列（批量） ────────────────────────────────────────────────────

export interface ReviewQueueParams {
  signal?: string
  // 打分分级过滤：high=建议通过
  band?: string
  source_type?: string
  product_key?: string
  // 分类维度：审核者要能把范围收窄到"我要审的那一类"
  kind?: string
  problem_type?: string
  keyword?: string
  page?: number
  page_size?: number
}

/** 退回/通过的结构化理由码。放后端下发，前端只负责展示与回传（DRY）。 */
export async function fetchKnowledgeReviewReasonCodes(): Promise<KnowledgeReasonCodes> {
  if (isMockEnabled()) {
    await mockDelay()
    return {
      labels: {
        verified: '已核对，内容准确',
        executed_fix: '有已执行的处置动作，可复用',
        product_doc: '产品通用知识/资料，无现场凭据',
        not_executed_action: '结论是排查计划/通用建议，不是已执行的处置动作',
        missing_solution: '来源侧没有解决方案',
        duplicate: '重复（同簇已保留一条）',
        off_topic: '与本产品/售后场景无关',
        deprecated_format: '早期探测产物/已废弃格式',
        low_value: '信息量过低',
        outdated: '内容已过期/已下架',
        other: '其他（见备注）',
      },
      by_action: {
        publish: ['verified', 'executed_fix', 'product_doc'],
        archive: [
          'not_executed_action',
          'missing_solution',
          'duplicate',
          'off_topic',
          'deprecated_format',
          'low_value',
          'outdated',
          'other',
        ],
      },
    }
  }
  const { data } = await client.get<KnowledgeReasonCodes>('/knowledge/review/reason-codes')
  return data
}

export async function fetchKnowledgeReviewSummary(): Promise<KnowledgeReviewSummary> {
  if (isMockEnabled()) {
    await mockDelay()
    const drafts = mockKnowledge.filter((entry) => entry.status === 'draft')
    return {
      status_counts: {
        draft: drafts.length,
        published: mockKnowledge.filter((e) => e.status === 'published').length,
        archived: mockKnowledge.filter((e) => e.status === 'archived').length,
      },
      pending_review: drafts.length,
      distinct_title_groups: new Set(drafts.map((e) => e.title)).size,
      duplicate_clusters: 0,
      duplicate_entries: 0,
      signal_counts: {
        ai_unverified: drafts.filter((e) => e.ai_generated).length,
        publish_ready: drafts.length,
        no_conclusion: 0,
        no_root_cause: 0,
        unclassified_product: 0,
        thin_content: 0,
        possible_duplicate: 0,
      },
      draft_by_source: {},
      draft_by_product: {},
      batch_limit: 200,
      // mock 里没有打分器：分级一律 high，避免审核台在 mock 下显示"未打分"而看起来像坏了
      band_counts: { high: drafts.length, mid: 0, low: 0 },
      scoring: {
        version: 'v0-rules',
        high_threshold: 75,
        mid_threshold: 50,
        band_labels: { high: '建议通过', mid: '待定', low: '建议退回' },
        band_order: { high: 0, mid: 1, low: 2 },
        auto_publish_enabled: false,
        min_labels_for_autopublish: 300,
      },
    }
  }
  const { data } = await client.get<KnowledgeReviewSummary>('/knowledge/review/summary')
  return data
}

export async function fetchKnowledgeReviewFacets(
  signal?: string
): Promise<KnowledgeReviewFacets> {
  if (isMockEnabled()) {
    await mockDelay()
    const drafts = mockKnowledge.filter((entry) => entry.status === 'draft')
    return {
      total: drafts.length,
      signal: signal ?? '',
      products: [],
      unclassified_product: { value: '', label: '未归类', count: 0 },
      kinds: [],
      problem_types: [],
      source_types: [],
    }
  }
  const { data } = await client.get<KnowledgeReviewFacets>('/knowledge/review/facets', {
    params: signal ? { signal } : {},
  })
  return data
}

export async function fetchKnowledgeReviewQueue(
  params: ReviewQueueParams = {}
): Promise<KnowledgeReviewQueue> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    if (!canManageKnowledge(roles)) {
      throw Object.assign(new Error('仅 manager/admin 可审核知识'), { code: 'FORBIDDEN' })
    }
    const items = mockKnowledge.filter((entry) => entry.status === 'draft')
    const filtered = params.band ? items.filter((entry) => entry.auto_band === params.band) : items
    return { page: 1, page_size: params.page_size ?? 20, total: filtered.length, items: filtered }
  }
  const { data } = await client.get<KnowledgeReviewQueue>('/knowledge/review/queue', {
    params,
  })
  return data
}

export async function batchPublishKnowledge(
  entryIds: number[],
  options: { note?: string; reasonCode?: string } = {}
): Promise<{ published: number; ai_generated: number }> {
  const { note, reasonCode } = options
  if (isMockEnabled()) {
    await mockDelay()
    const { roles, actor } = useAuthStore.getState()
    if (!canManageKnowledge(roles)) {
      throw Object.assign(new Error('当前角色无发布权限'), { code: 'FORBIDDEN' })
    }
    const picked = mockKnowledge.filter((e) => entryIds.includes(e.id))
    if (picked.some((e) => e.ai_generated) && !note?.trim()) {
      throw Object.assign(new Error('选中的条目含 AI 归纳内容，必须填写审核说明'), {
        code: 'VALIDATION_ERROR',
      })
    }
    const now = new Date().toISOString()
    for (const entry of picked) {
      entry.status = 'published'
      entry.status_label = KNOWLEDGE_STATUS_LABEL.published
      entry.published_at = now
      entry.reviewer = actor || 'mgr_a'
      entry.review_note = note?.trim() || null
    }
    return { published: picked.length, ai_generated: picked.filter((e) => e.ai_generated).length }
  }
  const { data } = await client.post<{ published: number; ai_generated: number }>(
    '/knowledge/review/publish',
    { entry_ids: entryIds, note, reason_code: reasonCode }
  )
  return data
}

/** 批量归档（退回 / 下架）。``note`` / ``reasonCode`` 可选，口径同 ``archiveKnowledge``。 */
export async function batchArchiveKnowledge(
  entryIds: number[],
  options: { note?: string; reasonCode?: string } = {}
): Promise<{ archived: number; unpublished: number }> {
  const { note, reasonCode } = options
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    if (!canManageKnowledge(roles)) {
      throw Object.assign(new Error('当前角色无归档权限'), { code: 'FORBIDDEN' })
    }
    const picked = mockKnowledge.filter((e) => entryIds.includes(e.id))
    const resolvedNote = note?.trim() || `批量退回 ${picked.length} 条；未填说明`
    for (const entry of picked) {
      entry.status = 'archived'
      entry.status_label = KNOWLEDGE_STATUS_LABEL.archived
      entry.review_note = resolvedNote
    }
    return { archived: picked.length, unpublished: 0 }
  }
  const { data } = await client.post<{ archived: number; unpublished: number }>(
    '/knowledge/review/archive',
    { entry_ids: entryIds, note, reason_code: reasonCode }
  )
  return data
}

export async function fetchKnowledgeIndexStatus(): Promise<KnowledgeIndexStatus> {
  if (isMockEnabled()) {
    await mockDelay()
    const indexed = mockKnowledge.filter((entry) => entry.is_indexed).length
    return {
      vector_store_configured: false,
      vector_store_healthy: false,
      embedding_configured: false,
      embedding_model: 'mock-embedding',
      embedding_dimension: 1024,
      total: mockKnowledge.length,
      indexed,
      pending_index: mockKnowledge.length - indexed,
    }
  }
  const { data } = await client.get<KnowledgeIndexStatus>('/knowledge/index/status')
  return data
}

export async function reindexPendingKnowledge(limit = 200): Promise<KnowledgeReindexResult> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    if (!canManageKnowledge(roles)) {
      throw Object.assign(new Error('当前角色无补建索引权限'), { code: 'FORBIDDEN' })
    }
    const pending = mockKnowledge.filter((entry) => !entry.is_indexed)
    for (const entry of pending) {
      entry.is_indexed = true
      entry.index_error = null
    }
    return { scanned: pending.length, indexed: pending.length, failed: 0, errors: [] }
  }
  const { data } = await client.post<KnowledgeReindexResult>('/knowledge/index/pending', null, {
    params: { limit },
  })
  return data
}
